import { trpc } from '@/lib/trpc';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { LlmModelForm, LlmModelFormData } from './add';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Flex } from '@televet/kibble-ui/chakra';
import { toaster } from '@/components/ui/toaster';

export const Route = createFileRoute('/_authenticated/llm-models/$id')({
  component: EditLlmModel,
  beforeLoad: () => {
    return {
      pageTitle: 'Edit LLM Model',
    };
  },
});

function EditLlmModel() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const { data: llmModel, isLoading } = useQuery(
    trpc.llmModel.findOne.queryOptions({ id })
  );

  const { mutate } = useMutation(
    trpc.llmModel.updateOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'LLM model updated successfully',
          description: data.message,
        });
        navigate({ to: '/llm-models' });
      },
      onError: (error) => {
        const errorMessage = error.message || 'Failed to update LLM model';
        toaster.error({
          title: 'Failed to update LLM model',
          description: errorMessage,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LlmModelFormData>({
    values: llmModel
      ? {
          name: llmModel.name,
          externalModelId: llmModel.externalModelId,
          modelProvider: llmModel.modelProvider,
        }
      : undefined,
  });

  const onSubmit = (data: LlmModelFormData) => {
    mutate({
      id,
      data: {
        name: data.name,
        externalModelId: data.externalModelId,
        modelProvider: 'AwsBedrock',
      },
    });
  };

  const onCancel = () => {
    navigate({ to: '/llm-models' });
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return (
    <LlmModelForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
      title="Edit LLM Model"
    />
  );
}
